/**
 * FeedbackSheet (design system; docs/specs/support.md): "Send feedback". Kind (Bug / Idea / Question), the text with
 * a kind-based label and placeholder, one screenshot (captured, chosen or pasted), "Include diagnostics" with what is
 * included, Cancel / Send. Rate limited: a warning banner, Send off, the text kept. Failed: a danger banner and
 * "Try again". Driven by core FeedbackViewModel; the platform captures the window (`onCapture`).
 */
import type { FeedbackKind, SupportRequestResult } from "@bundle/contracts";
import { Files, Support, type FeedbackViewModel, type PickedFile } from "@bundle/core";
import { size, space, type IconName } from "@bundle/tokens";
import { useEffect, useMemo, useState } from "react";
import { useFormat, useLocale, useT, useTr } from "../i18n/I18nProvider.tsx";
import { useViewModel } from "../hooks/index.ts";
import { Banner, Button, Checkbox, FilePicker, Icon, IconButton, Image, SegmentedControl, Sheet, Stack, Text, TextField } from "../primitives/index.ts";

export type FeedbackSheetProps = {
  open: boolean;
  onClose: () => void;
  vm: FeedbackViewModel;
  /** "Add screenshot": a picture of the window without this sheet. Absent = no such button (choose or paste only). */
  onCapture?: () => Promise<PickedFile | undefined>;
  /** Sent: the sheet closes itself first. */
  onSent: (result: SupportRequestResult) => void;
  testID: string;
};

const KIND_ICONS: Record<FeedbackKind, IconName> = { bug: "bug", idea: "callout", question: "help" };

/** An object URL for a picked browser file (revoked when it changes), unless the view model has a preview. */
function usePreview(file: PickedFile | undefined, preview: string | undefined): string | undefined {
  const blob = preview === undefined && file !== undefined && file.source instanceof Blob ? file.source : undefined;
  const url = useMemo(() => (blob === undefined ? undefined : URL.createObjectURL(blob)), [blob]);
  useEffect(() => () => (url === undefined ? undefined : URL.revokeObjectURL(url)), [url]);
  return preview ?? url;
}

export function FeedbackSheet({ open, onClose, vm, onCapture, onSent, testID }: FeedbackSheetProps) {
  const t = useT();
  const tr = useTr();
  const fmt = useFormat();
  const locale = useLocale();
  const s = useViewModel(vm);
  const [picking, setPicking] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [showIncluded, setShowIncluded] = useState(false);
  const copy = Support.kindCopy(s.kind);
  const shot = s.screenshot;
  const preview = usePreview(shot?.file, shot?.preview);
  const sending = s.status === "sending";

  const takeImage = (files: readonly PickedFile[]) => {
    const image = files.find((f) => f.mime.startsWith("image/")) ?? files[0];
    if (image !== undefined) vm.setScreenshot(image);
  };
  const capture = async () => {
    if (onCapture === undefined || capturing) return;
    setCapturing(true);
    try {
      const file = await onCapture();
      if (file !== undefined) vm.setScreenshot(file);
    } finally {
      setCapturing(false);
    }
  };
  const send = async () => {
    const result = await vm.submit();
    if (result === undefined) return;
    onClose();
    onSent(result);
  };

  return (
    <Sheet open={open} onClose={onClose} title={t("support.form.title")} closeLabel={t("common.close")} testID={testID}>
      <Stack gap="4" paddingX="5" paddingY="1">
        <Text variant="footnote" color="text.secondary">
          {t("support.form.subtitle")}
        </Text>
        <SegmentedControl<FeedbackKind>
          options={Support.FEEDBACK_KINDS.map((k) => ({ value: k, label: tr(Support.kindCopy(k).name), icon: KIND_ICONS[k] }))}
          value={s.kind}
          onChange={(k) => vm.setKind(k)}
          accessibilityLabel={t("support.kind.label")}
          fill
          testID={`${testID}-kind`}
        />
        <TextField
          value={s.text}
          onChange={(v) => vm.setText(v)}
          label={tr(copy.label)}
          placeholder={tr(copy.placeholder)}
          multiline
          minLines={5}
          autoFocus
          error={s.textError === undefined ? undefined : tr(s.textError)}
          helperText={s.counter}
          onPasteFiles={takeImage}
          testID={`${testID}-text`}
        />
        <Stack gap="2">
          <Stack direction="horizontal" gap="2" wrap>
            {onCapture === undefined ? null : (
              <Button variant="secondary" size="sm" icon="monitor" label={t("support.form.addScreenshot")} loading={capturing} onPress={() => void capture()} testID={`${testID}-capture`} />
            )}
            <Button variant="ghost" size="sm" icon="attach" label={t("support.form.chooseFile")} onPress={() => setPicking(true)} testID={`${testID}-choose`} />
          </Stack>
          {shot === undefined ? null : (
            <Stack direction="horizontal" align="center" gap="3" testID={`${testID}-screenshot`}>
              <Stack radius="sm" border="border.subtle" background="bg.surface">
                {preview === undefined ? (
                  <Stack padding="3" align="center" justify="center">
                    <Icon name="image" size="icon.md" color="text.tertiary" />
                  </Stack>
                ) : (
                  <Image src={preview} alt={shot.file.name} width={size["avatar.xl"]} height={space["12"]} radius="sm" />
                )}
                <Stack overlay="top-end">
                  <IconButton icon="close" variant="glass" size="sm" accessibilityLabel={t("support.form.removeScreenshot")} onPress={() => vm.removeScreenshot()} testID={`${testID}-remove-screenshot`} />
                </Stack>
              </Stack>
              <Stack gap="0.5" grow>
                <Text variant="caption" color="text.tertiary" lines={1}>
                  {t("support.form.screenshotSize", { size: Files.formatFileSize(shot.file.size, locale) })}
                </Text>
                {shot.error === undefined ? null : (
                  <Text variant="caption" color="status.danger.fg" testID={`${testID}-screenshot-error`}>
                    {tr(shot.error)}
                  </Text>
                )}
              </Stack>
            </Stack>
          )}
        </Stack>
        <Stack gap="1.5">
          <Stack direction="horizontal" align="center" gap="2" wrap>
            <Checkbox label={t("support.form.diagnostics")} value={s.includeDiagnostics} onChange={(v) => vm.setIncludeDiagnostics(v)} testID={`${testID}-diagnostics`} />
            <Button
              variant="ghost"
              size="sm"
              label={showIncluded ? t("support.form.hideIncluded") : t("support.form.whatsIncluded")}
              onPress={() => setShowIncluded((v) => !v)}
              aria-expanded={showIncluded}
              testID={`${testID}-included`}
            />
          </Stack>
          {showIncluded ? (
            <Stack gap="0.5" paddingX="3" paddingY="2" radius="md" background="bg.surface" testID={`${testID}-included-rows`}>
              {Support.diagnosticsRows(vm.diagnostics()).map((row) => (
                <Stack key={row.label.key} direction="horizontal" gap="3">
                  <Stack width="rail.width" fixedSize>
                    <Text variant="caption" color="text.tertiary">
                      {tr(row.label)}
                    </Text>
                  </Stack>
                  <Stack grow>
                    <Text variant="caption" color="text.secondary" selectable>
                      {row.value}
                    </Text>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          ) : null}
        </Stack>
        {s.status === "rateLimited" && s.retryAt !== undefined ? (
          <Banner tone="warning" message={t("support.form.rateLimited", { time: fmt.time(s.retryAt) })} testID={`${testID}-rate-limited`} />
        ) : s.status === "error" ? (
          <Banner tone="danger" message={t("support.form.error")} testID={`${testID}-error`} />
        ) : null}
        <Stack direction="horizontal" justify="end" gap="2" paddingY="3">
          <Button variant="ghost" label={t("support.form.cancel")} onPress={onClose} testID={`${testID}-cancel`} />
          <Button
            variant="primary"
            label={s.status === "error" ? t("support.form.tryAgain") : t("support.form.send")}
            loading={sending}
            disabled={!vm.canSend && !sending}
            onPress={() => void send()}
            testID={`${testID}-send`}
          />
        </Stack>
      </Stack>
      <FilePicker open={picking} onPick={takeImage} onClose={() => setPicking(false)} accept="image/*" testID={`${testID}-picker`} />
    </Sheet>
  );
}
